import React, { useState, useEffect, useRef } from 'react'
import { supabase } from './supabaseClient'

// ── Theme tokens ──────────────────────────────────────────────────────────────

const T = {
  bg:         '#0A0B14',
  card:       '#12131F',
  border:     '#1E2035',
  inputBg:    '#0E0F1C',
  gold:       '#F59E0B',
  goldHover:  '#D97706',
  text:       '#FFFFFF',
  muted:      '#6B7280',
  error:      '#EF4444',
  success:    '#10B981',
}

const styles = {
  page: {
    minHeight:       '100vh',
    background:      T.bg,
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    fontFamily:      "'Inter', 'Segoe UI', sans-serif",
    padding:         '24px',
  },
  card: {
    background:    T.card,
    border:        `1px solid ${T.border}`,
    borderRadius:  '16px',
    padding:       '40px',
    width:         '100%',
    maxWidth:      '420px',
  },
  logo: {
    textAlign:     'center',
    marginBottom:  '28px',
  },
  logoText: {
    fontSize:      '22px',
    fontWeight:    '700',
    color:         '#FFFFFF',
    letterSpacing: '0.05em',
  },
  logoSub: {
    fontSize:   '12px',
    color:      '#FFFFFF',
    marginTop:  '2px',
  },
  heading: {
    fontSize:      '20px',
    fontWeight:    '600',
    color:         T.text,
    marginBottom:  '24px',
    textAlign:     'center',
  },
  field: {
    marginBottom:  '16px',
    display:       'flex',
    flexDirection: 'column',
    gap:           '6px',
  },
  label: {
    fontSize:    '13px',
    fontWeight:  '500',
    color:       '#FFFFFF',
  },
  optionalTag: {
    fontSize:    '11px',
    color:       '#FFFFFF',
    fontWeight:  '400',
    marginLeft:  '4px',
  },
  input: {
    background:   T.inputBg,
    border:       `1px solid ${T.border}`,
    borderRadius: '8px',
    padding:      '10px 14px',
    color:        T.text,
    fontSize:     '14px',
    outline:      'none',
    transition:   'border-color 0.2s',
    width:        '100%',
    boxSizing:    'border-box',
  },
  button: {
    width:        '100%',
    padding:      '12px',
    background:   T.gold,
    color:        '#0A0B14',
    border:       'none',
    borderRadius: '8px',
    fontSize:     '14px',
    fontWeight:   '700',
    cursor:       'pointer',
    marginTop:    '8px',
    transition:   'background 0.2s',
    letterSpacing: '0.02em',
  },
  buttonDisabled: {
    background: T.muted,
    cursor:     'not-allowed',
  },
  error: {
    fontSize:     '13px',
    color:        T.error,
    background:   'rgba(239,68,68,0.08)',
    border:       `1px solid rgba(239,68,68,0.2)`,
    borderRadius: '6px',
    padding:      '8px 12px',
    marginBottom: '12px',
  },
  switchRow: {
    marginTop:  '20px',
    textAlign:  'center',
    fontSize:   '13px',
    color:      '#FFFFFF',
  },
  switchLink: {
    background:  'none',
    border:      'none',
    color:       T.gold,
    cursor:      'pointer',
    fontWeight:  '600',
    fontSize:    '13px',
    padding:     '0',
    marginLeft:  '4px',
  },
  rules: {
    listStyle:    'none',
    padding:      '0',
    margin:       '6px 0 16px',
    display:      'flex',
    flexDirection:'column',
    gap:          '4px',
  },
  ruleItem: (met) => ({
    fontSize:   '12px',
    color:      met ? T.success : '#FFFFFF',
    display:    'flex',
    alignItems: 'center',
    gap:        '6px',
    transition: 'color 0.2s',
  }),
  successBox: {
    background:   'rgba(16,185,129,0.08)',
    border:       `1px solid rgba(16,185,129,0.25)`,
    borderRadius: '8px',
    padding:      '16px',
    textAlign:    'center',
    color:        T.success,
    fontSize:     '14px',
    lineHeight:   '1.5',
    marginBottom: '16px',
  },
  divider: {
    height:     '1px',
    background: T.border,
    margin:     '24px 0',
  },
}

// ── Password rules ────────────────────────────────────────────────────────────

const PASSWORD_RULES = [
  { label: 'At least 8 characters',    test: p => p.length >= 8 },
  { label: 'One uppercase letter',      test: p => /[A-Z]/.test(p) },
  { label: 'One number',               test: p => /[0-9]/.test(p) },
  { label: 'One special character',    test: p => /[^A-Za-z0-9]/.test(p) },
]

function validatePassword(p) {
  return PASSWORD_RULES.every(r => r.test(p))
}

function RuleIcon({ met }) {
  return (
    <span style={{ fontSize: '10px' }}>{met ? '✓' : '○'}</span>
  )
}

// ── Login ─────────────────────────────────────────────────────────────────────

export function Login({ onLogin, onSwitchToRegister, noWrapper }) {
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [error, setError]       = useState('')
  const [loading, setLoading]   = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    const { error: err } = await supabase.auth.signInWithPassword({ email, password })
    setLoading(false)
    if (err) {
      setError(err.message)
    } else {
      onLogin()
    }
  }

  const card = (
    <div style={styles.card}>
      <div style={styles.logo}>
        <div style={styles.logoText}>CALLIDUS</div>
        <div style={styles.logoSub}>Financial Intelligence</div>
      </div>

      <h2 style={styles.heading}>Sign In</h2>

      <form onSubmit={handleSubmit}>
        <div style={styles.field}>
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoComplete="email"
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Password</label>
          <input
            style={styles.input}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="current-password"
          />
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <button
          type="submit"
          style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
          disabled={loading}
        >
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </form>

      <div style={styles.switchRow}>
        Don't have an account?
        <button style={styles.switchLink} type="button" onClick={onSwitchToRegister}>
          Register
        </button>
      </div>
    </div>
  )

  if (noWrapper) return card
  return <div style={styles.page}>{card}</div>
}

// ── Register ──────────────────────────────────────────────────────────────────

export function Register({ onSwitchToLogin, noWrapper }) {
  const [fields, setFields] = useState({
    firstName: '', middleName: '', lastName: '', email: '', password: '',
  })
  const [error, setError]     = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  function set(key) {
    return e => setFields(f => ({ ...f, [key]: e.target.value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (!validatePassword(fields.password)) {
      setError('Password does not meet all the requirements listed below.')
      return
    }

    setLoading(true)
    try {
      const { data, error } = await supabase.auth.signUp({
        email:    fields.email,
        password: fields.password,
        options: {
          data: {
            first_name:  fields.firstName,
            middle_name: fields.middleName,
            last_name:   fields.lastName,
          },
        },
      })
      console.log('SignUp data:', data)
      console.log('SignUp error:', error)
      if (error) throw error
      setSuccess(true)
    } catch (err) {
      console.log('SignUp exception:', err)
      setError(err.message)
    }
    setLoading(false)
  }

  if (success) {
    const successCard = (
      <div style={styles.card}>
        <div style={styles.logo}>
          <div style={styles.logoText}>CALLIDUS</div>
          <div style={styles.logoSub}>Financial Intelligence</div>
        </div>
        <div style={styles.successBox}>
          Please check your email to confirm your account.
        </div>
        <div style={styles.switchRow}>
          <button style={styles.switchLink} type="button" onClick={onSwitchToLogin}>
            Back to Sign In
          </button>
        </div>
      </div>
    )
    if (noWrapper) return successCard
    return <div style={styles.page}>{successCard}</div>
  }

  const card = (
    <div style={styles.card}>
      <div style={styles.logo}>
        <div style={styles.logoText}>CALLIDUS</div>
        <div style={styles.logoSub}>Financial Intelligence</div>
      </div>

      <h2 style={styles.heading}>Create Account</h2>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>First Name</label>
            <input
              style={styles.input}
              type="text"
              value={fields.firstName}
              onChange={set('firstName')}
              placeholder="Jane"
              required
            />
          </div>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>
              Last Name
            </label>
            <input
              style={styles.input}
              type="text"
              value={fields.lastName}
              onChange={set('lastName')}
              placeholder="Doe"
              required
            />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>
            Middle Name
            <span style={styles.optionalTag}>(optional)</span>
          </label>
          <input
            style={styles.input}
            type="text"
            value={fields.middleName}
            onChange={set('middleName')}
            placeholder="Marie"
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            value={fields.email}
            onChange={set('email')}
            placeholder="you@example.com"
            required
            autoComplete="email"
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Password</label>
          <input
            style={styles.input}
            type="password"
            value={fields.password}
            onChange={set('password')}
            placeholder="••••••••"
            required
            autoComplete="new-password"
          />
        </div>

        <ul style={styles.rules}>
          {PASSWORD_RULES.map(r => {
            const met = r.test(fields.password)
            return (
              <li key={r.label} style={styles.ruleItem(met)}>
                <RuleIcon met={met} />
                {r.label}
              </li>
            )
          })}
        </ul>

        {error && <div style={styles.error}>{error}</div>}

        <button
          type="submit"
          style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
          disabled={loading}
        >
          {loading ? 'Creating account…' : 'Create Account'}
        </button>
      </form>

      <div style={styles.switchRow}>
        Already have an account?
        <button style={styles.switchLink} type="button" onClick={onSwitchToLogin}>
          Sign In
        </button>
      </div>
    </div>
  )

  if (noWrapper) return card
  return <div style={styles.page}>{card}</div>
}

// ── AuthWrapper ───────────────────────────────────────────────────────────────

function AuthWrapper({ children }) {
  const [session, setSession]       = useState(undefined)  // undefined = loading, null = no session
  const [view, setView]             = useState('login')     // 'login' | 'register'
  const [formVisible, setFormVisible] = useState(false)
  const formRef                     = useRef(null)

  useEffect(() => {
    // onAuthStateChange fires immediately with INITIAL_SESSION in Supabase v2,
    // so it covers the initial load — no need for a separate getSession() call
    // that could race against it.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state changed:', _event, session)
      setSession(session ?? null)
    })
    console.log('Initial session check running')

    return () => subscription.unsubscribe()
  }, [])

  // After Supabase email-confirm redirect (e.g. /auth/confirm), strip the URL
  // back to / so the app doesn't sit on a dead route.
  useEffect(() => {
    if (session && window.location.pathname !== '/') {
      window.history.replaceState({}, '', '/')
    }
  }, [session])

  console.log('Rendering AuthWrapper, session:', session)

  // Still loading — session not yet resolved from Supabase
  if (session === undefined) {
    return (
      <div style={{
        minHeight:      '100vh',
        background:     T.bg,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
      }}>
        <div style={{
          width:          '32px',
          height:         '32px',
          borderRadius:   '50%',
          border:         `3px solid ${T.border}`,
          borderTopColor: T.gold,
          animation:      'spin 0.85s linear infinite',
        }} />
      </div>
    )
  }

  // No session — show landing page with welcome section; form appears on CTA click
  if (session === null) {
    function handleCTA(targetView) {
      setView(targetView)
      setFormVisible(true)
      setTimeout(() => {
        if (formRef.current) {
          formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 0)
    }

    const BENEFITS = [
      'Deep Fundamental Analysis',
      'Fair Value Estimates',
      'Plain Language Verdicts',
    ]

    return (
      <div style={{
        minHeight:  '100vh',
        background: T.bg,
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
      }}>

        {/* ── Welcome / hero section ── */}
        <div style={{
          textAlign: 'center',
          padding:   '72px 24px 64px',
          maxWidth:  '720px',
          margin:    '0 auto',
        }}>

          {/* Wordmark */}
          <div style={{
            fontSize:      '56px',
            fontWeight:    '800',
            color:         '#FFFFFF',
            letterSpacing: '0.06em',
            lineHeight:    '1',
            marginBottom:  '10px',
          }}>
            CALLIDUS
          </div>

          {/* Tagline */}
          <div style={{
            fontSize:      '12px',
            color:         '#FFFFFF',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            marginBottom:  '40px',
          }}>
            Investment Analysis&nbsp;&nbsp;·&nbsp;&nbsp;Long-Term Retail Investors
          </div>

          {/* Thin gold rule */}
          <div style={{
            height:     '1px',
            background: `linear-gradient(90deg, transparent, ${T.gold}, transparent)`,
            maxWidth:   '320px',
            margin:     '0 auto 40px',
          }} />

          {/* Description */}
          <p style={{
            fontSize:   '16px',
            color:      '#FFFFFF',
            lineHeight: '1.8',
            maxWidth:   '580px',
            margin:     '0 auto 14px',
          }}>
            Callidus analyses any NSE or BSE listed company across Profitability,
            Efficiency, Solvency and Growth — and delivers a clear{' '}
            <span style={{ color: T.success, fontWeight: '700' }}>BUY</span>
            <span style={{ color: '#FFFFFF', fontWeight: '700' }}> / </span>
            <span style={{ color: T.gold, fontWeight: '700' }}>WATCH</span>
            <span style={{ color: '#FFFFFF', fontWeight: '700' }}> / </span>
            <span style={{ color: T.error, fontWeight: '700' }}>AVOID</span>
            {' '}verdict with fair value estimates.
          </p>
          <p style={{
            fontSize:   '15px',
            color:      '#FFFFFF',
            lineHeight: '1.7',
            maxWidth:   '520px',
            margin:     '0 auto 44px',
          }}>
            Built for serious long-term retail investors who want to protect their
            hard-earned money and put it to work in the right businesses — not guess,
            not gamble.
          </p>

          {/* Benefit badges */}
          <div style={{
            display:        'flex',
            gap:            '12px',
            justifyContent: 'center',
            flexWrap:       'wrap',
            marginBottom:   '40px',
          }}>
            {BENEFITS.map(b => (
              <div key={b} style={{
                border:        `1px solid ${T.gold}`,
                borderRadius:  '8px',
                padding:       '10px 22px',
                fontSize:      '13px',
                fontWeight:    '600',
                color:         T.gold,
                letterSpacing: '0.03em',
                background:    'rgba(245,158,11,0.06)',
              }}>
                {b}
              </div>
            ))}
          </div>

          {/* CTA copy */}
          <p style={{
            fontSize:     '14px',
            color:        '#FFFFFF',
            marginBottom: '24px',
          }}>
            Register free to get started or log in to your account.
          </p>

          {/* CTA buttons */}
          <div style={{
            display:        'flex',
            gap:            '14px',
            justifyContent: 'center',
            flexWrap:       'wrap',
          }}>
            <button
              onClick={() => handleCTA('register')}
              style={{
                padding:       '13px 40px',
                background:    T.gold,
                color:         '#0A0B14',
                border:        'none',
                borderRadius:  '8px',
                fontSize:      '15px',
                fontWeight:    '700',
                cursor:        'pointer',
                letterSpacing: '0.03em',
                transition:    'background 0.2s',
              }}
            >
              Register Free
            </button>
            <button
              onClick={() => handleCTA('login')}
              style={{
                padding:       '13px 40px',
                background:    'transparent',
                color:         T.gold,
                border:        `2px solid ${T.gold}`,
                borderRadius:  '8px',
                fontSize:      '15px',
                fontWeight:    '700',
                cursor:        'pointer',
                letterSpacing: '0.03em',
                transition:    'all 0.2s',
              }}
            >
              Log In
            </button>
          </div>
        </div>

        {/* ── Auth form card — only shown after a CTA button is clicked ── */}
        {formVisible && (
          <>
            <div style={{
              height:     '1px',
              background: T.border,
              maxWidth:   '480px',
              margin:     '0 auto',
            }} />
            <div
              ref={formRef}
              style={{
                display:        'flex',
                justifyContent: 'center',
                padding:        '56px 24px 80px',
              }}
            >
              {view === 'register'
                ? <Register onSwitchToLogin={() => setView('login')} noWrapper />
                : <Login onLogin={() => {}} onSwitchToRegister={() => setView('register')} noWrapper />
              }
            </div>
          </>
        )}

      </div>
    )
  }

  // Valid session — render the app
  return children
}

export default AuthWrapper
